import { useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Divider,
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import PeopleIcon from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import ShareIcon from "@mui/icons-material/Share";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { useGame, useGameDispatch } from "../context/GameContext";
import SeoHead from "../components/SeoHead";
import AdBanner from "../components/AdBanner";
import { useTranslation } from "react-i18next";

const gameModes = [
  {
    titleKey: "home.artistBrackets",
    descKey: "home.artistBracketsDesc",
    icon: <MusicNoteIcon sx={{ fontSize: 28 }} />,
    path: "/game/artist",
    color: "#e74c3c",
  },
  {
    titleKey: "home.customGames",
    descKey: "home.customGamesDesc",
    icon: <ShuffleIcon sx={{ fontSize: 28 }} />,
    path: "/game/custom",
    color: "#3498db",
  },
  {
    titleKey: "home.artistVsArtist",
    descKey: "home.artistVsArtistDesc",
    icon: <PeopleIcon sx={{ fontSize: 28 }} />,
    path: "/game/versus",
    color: "#6C4675",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { siteData } = useGame();
  const dispatch = useGameDispatch();
  const { t } = useTranslation();

  const hasSavedGame = !!localStorage.getItem("currentGame");

  function handleContinue() {
    const savedGame = localStorage.getItem("currentGame");
    if (!savedGame) return;
    const parsed = JSON.parse(savedGame);
    const bracketKey = `brackets${parsed.bracketSize}`;
    const savedBrackets = localStorage.getItem(bracketKey);
    dispatch({
      type: "CONTINUE_GAME",
      payload: {
        siteData: parsed,
        brackets: savedBrackets
          ? JSON.parse(savedBrackets)
          : { winners: [], losers: [] },
      },
    });
    navigate("/game/play");
  }

  return (
    <Box id="home-page">
      <SeoHead canonical="/" />
      <Typography id="home-title" variant="h4" textAlign="center" gutterBottom sx={{ mt: 2 }}>
        {t("home.title")}
      </Typography>
      <Typography
        id="home-subtitle"
        variant="body1"
        textAlign="center"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        {t("home.subtitle")}
      </Typography>

      <Box
        id="home-game-modes"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 1.5,
          mb: 3,
          maxWidth: 560,
          mx: "auto",
        }}
      >
        {gameModes.map((mode) => (
          <Card key={mode.path} sx={{ border: `1px solid ${mode.color}22` }}>
            <CardActionArea onClick={() => navigate(mode.path)}>
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 1,
                  px: 1,
                  "&:last-child": { pb: 1 },
                }}
              >
                <Box sx={{ color: mode.color, mb: 0.25 }}>{mode.icon}</Box>
                <Typography variant="body2" fontWeight="bold">
                  {t(mode.titleKey)}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {hasSavedGame && !siteData.gameFinished && (
        <Box id="home-continue-wrapper" sx={{ textAlign: "center" }}>
          <Card
            id="home-continue-card"
            sx={{
              display: "inline-block",
              border: "1px solid #1abc9c44",
              cursor: "pointer",
            }}
            onClick={handleContinue}
          >
            <CardContent sx={{ py: 1, px: 2, "&:last-child": { pb: 1 } }}>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {t("home.continueGame")}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* How It Works */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography id="home-how-title" variant="h5" textAlign="center" gutterBottom>
        How It Works
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3, maxWidth: 800, mx: "auto", mb: 4 }}>
        <Box sx={{ textAlign: "center" }}>
          <QueueMusicIcon sx={{ fontSize: 40, color: "#3498db", mb: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>1. Choose Your Songs</Typography>
          <Typography variant="body2" color="text.secondary">
            Pick an artist from our database, create a custom bracket with your own songs, or pit two artists against each other. Select your bracket size from 8 to 64 songs.
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <HowToVoteIcon sx={{ fontSize: 40, color: "#e74c3c", mb: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>2. Vote on Matchups</Typography>
          <Typography variant="body2" color="text.secondary">
            Songs are paired up in head-to-head matchups. Listen to previews and pick your favorite in each round. Choose between single or double elimination formats.
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <EmojiEventsIcon sx={{ fontSize: 40, color: "#f39c12", mb: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>3. Crown a Winner</Typography>
          <Typography variant="body2" color="text.secondary">
            After all the rounds are complete, your top song is crowned the champion. Save your results, share your bracket, and see how your picks compare with other players.
          </Typography>
        </Box>
      </Box>

      <AdBanner adSlot="1234567890" />

      {/* Game Modes Detail */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography id="home-modes-title" variant="h5" textAlign="center" gutterBottom>
        Game Modes
      </Typography>
      <Box sx={{ maxWidth: 700, mx: "auto", mb: 4 }}>
        <Card sx={{ mb: 2, backgroundColor: "rgba(231,76,60,0.05)", border: "1px solid rgba(231,76,60,0.15)" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <MusicNoteIcon sx={{ color: "#e74c3c" }} />
              <Typography variant="h6">Artist Brackets</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Search for any artist and we'll automatically build a bracket from their top songs. Whether it's classic rock, modern pop, hip-hop, or indie — find your favorite artist and rank their discography. With bracket sizes from 8 to 64, you can do a quick tournament or a deep dive into their entire catalog.
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ mb: 2, backgroundColor: "rgba(52,152,219,0.05)", border: "1px solid rgba(52,152,219,0.15)" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <ShuffleIcon sx={{ color: "#3498db" }} />
              <Typography variant="h6">Custom Games</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Build your own bracket from scratch with any combination of songs you want. Mix genres, decades, and artists to create the ultimate music showdown. Perfect for themed brackets like "Best Songs of the 2000s" or "Greatest Guitar Solos" — the possibilities are endless.
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ mb: 2, backgroundColor: "rgba(108,70,117,0.05)", border: "1px solid rgba(108,70,117,0.15)" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <PeopleIcon sx={{ color: "#6C4675" }} />
              <Typography variant="h6">Artist vs Artist</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Put two artists head-to-head and find out which one has the better songs. Songs from both artists are mixed together into a single bracket, so every matchup could be a clash between your two favorites. Settle the debate once and for all — who makes the better music?
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Features */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography id="home-features-title" variant="h5" textAlign="center" gutterBottom>
        Features
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, maxWidth: 700, mx: "auto", mb: 4 }}>
        {[
          { title: "Song Previews", desc: "Listen to 30-second previews of each song directly in the bracket so you can make informed picks." },
          { title: "Single & Double Elimination", desc: "Choose single elimination for a quick game, or double elimination to give every song a second chance." },
          { title: "Save & Share Results", desc: "Sign in with Google to save your completed brackets and share them with friends on the Explore page." },
          { title: "Community Brackets", desc: "Browse the Explore page to see brackets created by other players. Like your favorites and discover new music." },
        ].map((f) => (
          <Card key={f.title} sx={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{f.title}</Typography>
              <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* FAQ */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography variant="h5" textAlign="center" gutterBottom>
        Frequently Asked Questions
      </Typography>
      <Box sx={{ maxWidth: 700, mx: "auto", mb: 4 }}>
        {[
          {
            q: "What is a music bracket?",
            a: "A music bracket is a tournament-style competition where songs face off in head-to-head matchups. You pick your favorite in each pair, and winners advance to the next round until one song is crowned the champion.",
          },
          {
            q: "Is Music Brackets free?",
            a: "Yes, Music Brackets is completely free to use. You can play unlimited brackets, explore community results, and save your game history without paying anything.",
          },
          {
            q: "Do I need an account to play?",
            a: "No. You can play brackets without signing in. A Google account is only needed to save results to the cloud and share brackets publicly on the Explore page.",
          },
          {
            q: "What is single vs double elimination?",
            a: "In single elimination, a song is out after one loss. In double elimination, every song gets a second chance and must lose twice before being eliminated, producing more accurate overall rankings.",
          },
          {
            q: "Can I listen to songs during the bracket?",
            a: "Yes. Each matchup includes 30-second song previews so you can listen before making your pick. This helps you make informed decisions, especially for songs you haven't heard recently.",
          },
          {
            q: "How many artists are available?",
            a: "Our database covers thousands of artists across pop, rock, hip-hop, R&B, country, electronic, indie, K-pop, Latin, and many more genres. We add new artists and songs regularly.",
          },
        ].map((faq) => (
          <Accordion
            key={faq.q}
            disableGutters
            sx={{
              backgroundColor: "rgba(255,255,255,0.02)",
              "&:before": { display: "none" },
              border: "1px solid rgba(255,255,255,0.06)",
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="bold">
                {faq.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Closing */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <ShareIcon sx={{ fontSize: 28, color: "grey.500", mb: 1 }} />
        <Typography variant="body1" fontWeight="bold" gutterBottom>
          Ready to find your favorite song?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 550, mx: "auto", mb: 2 }}>
          Music Brackets is a free tool for music fans to rank and discover songs through
          bracket-style tournaments. Choose an artist, build a custom bracket, or start an artist
          vs artist showdown — your next favorite song might surprise you.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Powered by{" "}
          <Box
            component="a"
            href="https://www.musicrandomizer.com"
            target="_blank"
            sx={{ color: "grey.300", textDecoration: "underline" }}
          >
            MusicRandomizer.com
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
